import React from 'react';
import { Drawer, Box, Typography, Divider, useTheme, Stack, Grid } from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  LocalOffer as OfferIcon,
  Schedule as ScheduleIcon,
  Campaign as PromotionIcon,
} from '@mui/icons-material';
import { OfferListItem } from '../../types';
import { useOfferConfig } from '../../contexts';
import { getLabelsFromOptionIds } from '../../utils/labelHelpers';
import StatusChip from '@shared/components/StatusChip/StatusChip';
import { DrawerHeader, DrawerContent, DrawerFooter } from '@shared/components/drawer';
import { formatDate, parsePremiumIds } from '../../utils';
import { OfferBasicInfo } from '@modules/offers/shared/components/Cards/OfferBasicInfo';
import { InfoCard } from '@modules/offers/shared/components/Cards/InfoCard';
import { InfoRow } from '@modules/offers/shared/components/Cards/InfoRow';

interface OfferDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  offer: OfferListItem | null;
  onEdit?: (offer: OfferListItem) => void;
}

export const OfferDetailsDrawer: React.FC<OfferDetailsDrawerProps> = ({ open, onClose, offer, onEdit }) => {
  const theme = useTheme();
  const { premiumOptions } = useOfferConfig();

  if (!offer) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: '90%', md: 500 },
          },
        },
        transition: {
          easing: {
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      }}
      transitionDuration={300}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DrawerHeader title="Offer Details" onClose={onClose} />

        <DrawerContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 12 }}>
              <OfferBasicInfo offer={offer} />
            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
              <InfoCard title="Premium Configuration" icon={<OfferIcon sx={{ fontSize: 15 }} />}>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5, fontWeight: 600 }}>
                    Premium IDs:
                  </Typography>
                  {offer.premium_ids ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {parsePremiumIds(offer.premium_ids?.split(',') ?? []).map((id, idx) => (
                        <StatusChip
                          key={idx}
                          label={`${getLabelsFromOptionIds(id.premiumId, premiumOptions)}=${id.amount}`}
                          status="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}>
                      No premium items configured
                    </Typography>
                  )}
                </Stack>
              </InfoCard>
            </Grid>
            {offer.offer_schedule?.length > 0 && (
              <Grid size={{ xs: 12, md: 12 }}>
                <InfoCard title="Schedule Configuration" icon={<ScheduleIcon sx={{ fontSize: 15 }} />}>
                  <Stack spacing={2}>
                    {offer.offer_schedule.map((schedule, idx) => (
                      <Stack spacing={1} key={idx}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                          <CalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 15 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {schedule.entry_name || `Schedule ${idx + 1}`}
                          </Typography>
                          <StatusChip label={schedule.schedule_type} status="primary" />
                        </Box>
                        <Divider />
                        <InfoRow
                          label="Start"
                          value={schedule.start_date ? formatDate(schedule.start_date, true) : '-'}
                        />
                        <Divider />
                        <InfoRow label="End" value={schedule.end_date ? formatDate(schedule.end_date, true) : '-'} />
                      </Stack>
                    ))}
                  </Stack>
                </InfoCard>
              </Grid>
            )}

            {offer.offer_promotion?.length > 0 && (
              <Grid size={{ xs: 12, md: 12 }}>
                <InfoCard title="Promotion Details" icon={<PromotionIcon sx={{ fontSize: 15 }} />}>
                  <Stack spacing={2}>
                    {offer.offer_promotion.map((promo, idx) => (
                      <Stack spacing={1} key={idx}>
                        <InfoRow label="Priority" value={promo.priority} />
                        <InfoRow label="Max Show Limit" value={promo.max_show_limit} />
                        <InfoRow label="Frequency" value={promo.frequency} />
                        <InfoRow label="Show After (seconds)" value={promo.show_after_last_popup_seconds} />
                      </Stack>
                    ))}
                  </Stack>
                </InfoCard>
              </Grid>
            )}
          </Grid>
        </DrawerContent>

        {onEdit && (
          <DrawerFooter
            primaryButton={{
              label: 'Edit Offer',
              onClick: () => onEdit(offer),
              variant: 'contained',
            }}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default OfferDetailsDrawer;
